import { supabase } from '../supabaseClient';
import { resolveStorageUrl } from '../utils/storageUrl';

export const aproposService = {
  async getAproposData(editionYear) {
    try {
      console.log('Getting apropos data for edition:', editionYear);
      
      // Get edition ID
      const { data: edition } = await supabase
        .from('editions')
        .select('id')
        .eq('year', editionYear)
        .single();

      if (!edition) {
        console.log(`Edition ${editionYear} not found`);
        throw new Error(`Edition ${editionYear} not found`);
      }

      console.log('Edition found:', edition);

      // Get main apropos data (don't throw error if not found)
      const { data: mainData, error: mainDataError } = await supabase
        .from('apropos_data')
        .select('*')
        .eq('edition_id', edition.id)
        .single();

      if (mainDataError && mainDataError.code !== 'PGRST116') {
        // PGRST116 is "not found" error, which is okay
        console.error('Error getting main data:', mainDataError);
      }

      console.log('Main data:', mainData);

      // Get event details
      const { data: eventDetails } = await supabase
        .from('apropos_event_details')
        .select('*')
        .eq('edition_id', edition.id)
        .order('display_order');

      console.log('Event details:', eventDetails);

      // Get committee members
      const { data: committeeMembers } = await supabase
        .from('apropos_committee_members')
        .select('*')
        .eq('edition_id', edition.id)
        .order('display_order');

      console.log('Committee members:', committeeMembers);

      return {
        pageHeader: {
          title: mainData?.page_header_title || ''
        },
        eventIntro: {
          paragraph1: mainData?.event_intro_paragraph1 || '',
          paragraph2: mainData?.event_intro_paragraph2 || ''
        },
        images: {
          image1_url: resolveStorageUrl(mainData?.image1_url || ''),
          image1_title: mainData?.image1_title || '',
          image2_url: resolveStorageUrl(mainData?.image2_url || ''),
          image2_title: mainData?.image2_title || '',
          image3_url: resolveStorageUrl(mainData?.image3_url || ''),
          image3_title: mainData?.image3_title || ''
        },
        committeeSection: {
          title: mainData?.committee_section_title || '',
          honoraryCommitteeTop: mainData?.honorary_committee_top || '',
          honoraryCommitteeBottom: mainData?.honorary_committee_bottom || ''
        },
        eventDetails: (eventDetails || []).map(detail => ({
          id: detail.id,
          title: detail.title,
          content: detail.content,
          icon: detail.icon
        })),
        committeeMembers: (committeeMembers || []).map(member => ({
          id: member.id,
          name: member.name,
          title: member.title,
          organization: member.organization,
          image: resolveStorageUrl(member.image_url, 'speaker-images', member.image_path) || member.image_path,
          logo: resolveStorageUrl(member.company_logo_url, 'speaker-images', member.company_logo_path), // use company_logo_url for logo
          is_honorary: member.is_honorary === 1 || member.is_honorary === '1' || member.is_honorary === true,
          is_organizing: member.is_organizing === 1 || member.is_organizing === '1' || member.is_organizing === true,
          is_president: member.is_president === 1 || member.is_president === '1' || member.is_president === true,
          is_scientific: member.is_scientific === 1 || member.is_scientific === '1' || member.is_scientific === true,
          is_expert: member.is_expert === 1 || member.is_expert === '1' || member.is_expert === true,
          order_president: member.order_president || 0,
          order_scientific: member.order_scientific || 0,
          order_honorary: member.order_honorary || 0,
          order_organizing: member.order_organizing || 0,
          order_expert: member.order_expert || 0,
          display_order: member.display_order || 0
        }))
      };
    } catch (error) {
      console.error('Error getting apropos data:', error);
      throw error;
    }
  },

  async updateAproposData(aproposData, editionYear) {
    try {
      // Get edition ID
      const { data: edition } = await supabase
        .from('editions')
        .select('id')
        .eq('year', editionYear)
        .single();

      if (!edition) {
        throw new Error(`Edition ${editionYear} not found`);
      }

      const updateData = {
        edition_id: edition.id,
        page_header_title: aproposData.pageHeader?.title || '',
        event_intro_paragraph1: aproposData.eventIntro?.paragraph1 || '',
        event_intro_paragraph2: aproposData.eventIntro?.paragraph2 || '',
        image1_url: aproposData.images?.image1_url || '',
        image1_title: aproposData.images?.image1_title || '',
        image2_url: aproposData.images?.image2_url || '',
        image2_title: aproposData.images?.image2_title || '',
        image3_url: aproposData.images?.image3_url || '',
        image3_title: aproposData.images?.image3_title || '',
        committee_section_title: aproposData.committeeSection?.title || '',
        honorary_committee_top: aproposData.committeeSection?.honoraryCommitteeTop || '',
        honorary_committee_bottom: aproposData.committeeSection?.honoraryCommitteeBottom || '',
        updated_at: new Date().toISOString()
      };

      // Try to update existing record first
      const { data: existingData } = await supabase
        .from('apropos_data')
        .select('id')
        .eq('edition_id', edition.id)
        .single();

      if (existingData) {
        // Update existing record
        const { data, error } = await supabase
          .from('apropos_data')
          .update(updateData)
          .eq('id', existingData.id)
          .select()
          .single();

        if (error) throw error;
        return data;
      } else {
        // Insert new record
        const { data, error } = await supabase
          .from('apropos_data')
          .insert([updateData])
          .select()
          .single();

        if (error) throw error;
        return data;
      }
    } catch (error) {
      console.error('Error updating apropos data:', error);
      throw error;
    }
  },

  async addEventDetail(detailData, editionYear) {
    try {
      // Get edition ID
      const { data: edition } = await supabase
        .from('editions')
        .select('id')
        .eq('year', editionYear)
        .single();

      if (!edition) {
        throw new Error(`Edition ${editionYear} not found`);
      }

      const { data, error } = await supabase
        .from('apropos_event_details')
        .insert([{
          edition_id: edition.id,
          title: detailData.title,
          content: detailData.content,
          icon: detailData.icon,
          display_order: detailData.display_order || 0
        }])
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error adding event detail:', error);
      throw error;
    }
  },

  async updateEventDetail(detailData) {
    try {
      const { data, error } = await supabase
        .from('apropos_event_details')
        .update({
          title: detailData.title,
          content: detailData.content,
          icon: detailData.icon,
          updated_at: new Date().toISOString()
        })
        .eq('id', detailData.id)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating event detail:', error);
      throw error;
    }
  },

  async deleteEventDetail(detailId) {
    try {
      const { error } = await supabase
        .from('apropos_event_details')
        .delete()
        .eq('id', detailId);

      if (error) throw error;
    } catch (error) {
      console.error('Error deleting event detail:', error);
      throw error;
    }
  },

  async addCommitteeMember(memberData, editionYear) {
    try {
      // Get edition ID
      const { data: edition } = await supabase
        .from('editions')
        .select('id')
        .eq('year', editionYear)
        .single();

      if (!edition) {
        throw new Error(`Edition ${editionYear} not found`);
      }

      const { data, error } = await supabase
        .from('apropos_committee_members')
        .insert([{
          edition_id: edition.id,
          person_id: memberData.person_id || null,
          name: memberData.name,
          title: memberData.title,
          organization: memberData.organization,
          image_url: memberData.image,
          company_logo_url: memberData.logo,
          is_honorary: memberData.is_honorary ? 1 : 0,
          is_organizing: memberData.is_organizing ? 1 : 0,
          is_president: memberData.is_president ? 1 : 0,
          is_scientific: memberData.is_scientific ? 1 : 0,
          is_expert: memberData.is_expert ? 1 : 0,
          order_president: memberData.order_president || memberData.display_order || 0,
          order_scientific: memberData.order_scientific || memberData.display_order || 0,
          order_honorary: memberData.order_honorary || memberData.display_order || 0,
          order_organizing: memberData.order_organizing || memberData.display_order || 0,
          order_expert: memberData.order_expert || memberData.display_order || 0,
          display_order: memberData.display_order || 0
        }])
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error adding committee member:', error);
      throw error;
    }
  },

  async updateCommitteeMember(memberData) {
    try {
      const { data, error } = await supabase
        .from('apropos_committee_members')
        .update({
          name: memberData.name,
          title: memberData.title,
          organization: memberData.organization,
          image_url: memberData.image,
          company_logo_url: memberData.logo,
          is_honorary: memberData.is_honorary ? 1 : 0,
          is_organizing: memberData.is_organizing ? 1 : 0,
          is_president: memberData.is_president ? 1 : 0,
          is_scientific: memberData.is_scientific ? 1 : 0,
          is_expert: memberData.is_expert ? 1 : 0,
          order_president: memberData.order_president || 0,
          order_scientific: memberData.order_scientific || 0,
          order_honorary: memberData.order_honorary || 0,
          order_organizing: memberData.order_organizing || 0,
          order_expert: memberData.order_expert || 0,
          display_order: memberData.display_order,
          updated_at: new Date().toISOString()
        })
        .eq('id', memberData.id)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating committee member:', error);
      throw error;
    }
  },

  async updateMemberOrder(memberId, newOrder) {
    try {
      const { data, error } = await supabase
        .from('apropos_committee_members')
        .update({
          display_order: newOrder,
          updated_at: new Date().toISOString()
        })
        .eq('id', memberId)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating member order:', error);
      throw error;
    }
  },

  async updateMemberSectionOrder(memberId, newOrder, section) {
    try {
      // Map section flag names to order column names
      const sectionToColumn = {
        is_president: 'order_president',
        is_scientific: 'order_scientific',
        is_honorary: 'order_honorary',
        is_organizing: 'order_organizing',
        is_expert: 'order_expert'
      };
      
      const columnName = sectionToColumn[section] || 'display_order';
      
      const { data, error } = await supabase
        .from('apropos_committee_members')
        .update({
          [columnName]: newOrder,
          updated_at: new Date().toISOString()
        })
        .eq('id', memberId)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating member section order:', error);
      throw error;
    }
  },

  async deleteCommitteeMember(memberId) {
    try {
      const { error } = await supabase
        .from('apropos_committee_members')
        .delete()
        .eq('id', memberId);

      if (error) throw error;
    } catch (error) {
      console.error('Error deleting committee member:', error);
      throw error;
    }
  }
};
